import { Modal } from "@mui/material";
import { FC, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilState } from "recoil";

import { SVG_MUI } from "@ryde/assets";
import { ConfirmResult, Log, softValidationErrorAtom } from "@ryde/shared";

import { SoftValidationContent } from "../common";
import {
  ModalContainerStyled,
  ModalContentStyled,
  ModalFooterStyled,
  ModalHeaderStyled,
  MuiButton,
} from "../controls";

const MODAL_WIDTH = "530px";

export const ConfirmSoftValidationContainer: FC = () => {
  const { t } = useTranslation();
  const [softValidationError, setSoftValidationError] = useRecoilState(
    softValidationErrorAtom,
  );

  const isModalOpen = useMemo(() => {
    return !!softValidationError;
  }, [softValidationError]);

  const okClickHandler = useCallback(
    (result: ConfirmResult) => () => {
      if (!softValidationError) {
        Log.warnAndToast(t("errors.INVALID_DELETE_HANDLER_DATA"));
        return;
      }

      const { resolveFn } = softValidationError;
      resolveFn(result);
      Log.debug("Soft validation confirm with result:", result);
      setSoftValidationError(null);
    },
    [softValidationError, setSoftValidationError, t],
  );

  return (
    <>
      {isModalOpen && (
        <Modal open={isModalOpen} closeAfterTransition={true}>
          <ModalContainerStyled width={MODAL_WIDTH}>
            <ModalHeaderStyled>
              <h1>{t(`general.VERIFICATION_REQUIRED`)}</h1>
              <SVG_MUI.XMarkSVG
                onClick={okClickHandler(ConfirmResult.CANCEL)}
              />
            </ModalHeaderStyled>
            <ModalContentStyled>
              {softValidationError && (
                <SoftValidationContent data={softValidationError.data} />
              )}
            </ModalContentStyled>
            <ModalFooterStyled>
              <MuiButton
                muiStyle={"primary"}
                muiVariant={"outlined"}
                onClick={okClickHandler(ConfirmResult.CANCEL)}
                e2eData="cancel-modal-button"
              >
                {t(`general.CANCEL`)}
              </MuiButton>
              <MuiButton
                onClick={okClickHandler(ConfirmResult.OK)}
                e2eData="accept-data-change"
              >
                {t(`general.OK`)}
              </MuiButton>
            </ModalFooterStyled>
          </ModalContainerStyled>
        </Modal>
      )}
    </>
  );
};
